import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Database, 
  ChatCircleDots, 
  ChatText, 
  Clock,
  ArrowRight
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import api from "../lib/api";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const StatCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colorClasses = {
    blue: "text-blue-400 bg-blue-500/10",
    green: "text-emerald-400 bg-emerald-500/10",
    yellow: "text-amber-400 bg-amber-500/10",
    purple: "text-purple-400 bg-purple-500/10",
  };
  
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${colorClasses[color]}`}>
          <Icon weight="duotone" className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-semibold text-zinc-100 font-mono">{value.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

const RecentUpload = ({ dataset }) => {
  const platformColors = {
    chatgpt: "badge-green",
    perplexity: "badge-blue",
    gemini: "badge-purple",
    copilot: "badge-yellow",
    markdown: "badge-blue",
    text: "badge-yellow",
    json: "badge-green",
  };
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <Database weight="regular" className="w-4 h-4 text-zinc-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-zinc-200 truncate">{dataset.name}</p>
          <p className="text-xs text-zinc-500">{dataset.conversation_count} conversations</p>
        </div>
      </div>
      <span className={`badge ${platformColors[dataset.source_platform] || 'badge-blue'}`}>
        {dataset.source_platform}
      </span>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_datasets: 0,
    total_conversations: 0,
    total_messages: 0,
    platforms: {},
    recent_uploads: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getStats();
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const platformData = Object.entries(stats.platforms).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const barData = stats.recent_uploads.slice(0, 5).map(d => ({
    name: d.name.substring(0, 15) + (d.name.length > 15 ? '...' : ''),
    conversations: d.conversation_count,
    messages: d.message_count
  }));

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-zinc-800 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-24 bg-zinc-800 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title="Dashboard" 
        subtitle="Overview of your AI training datasets"
      />
      
      <div className="p-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            icon={Database} 
            label="Total Datasets" 
            value={stats.total_datasets}
            color="blue"
          />
          <StatCard 
            icon={ChatCircleDots} 
            label="Conversations" 
            value={stats.total_conversations}
            color="green"
          />
          <StatCard 
            icon={ChatText} 
            label="Total Messages" 
            value={stats.total_messages}
            color="yellow"
          />
          <StatCard 
            icon={Clock} 
            label="Platforms" 
            value={Object.keys(stats.platforms).length}
            color="purple"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Platform Distribution */}
          <div className="chart-container">
            <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
              Platform Distribution
            </h3>
            {platformData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={platformData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '0.375rem'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-zinc-500 text-sm">
                No data yet. Upload some datasets to get started.
              </div>
            )}
          </div>

          {/* Recent Datasets Chart */}
          <div className="chart-container">
            <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
              Recent Datasets
            </h3>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barData} layout="vertical">
                  <XAxis type="number" stroke="#52525b" fontSize={11} />
                  <YAxis type="category" dataKey="name" stroke="#52525b" fontSize={11} width={100} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '0.375rem'
                    }}
                  />
                  <Bar dataKey="conversations" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-zinc-500 text-sm">
                No datasets uploaded yet.
              </div>
            )}
          </div>
        </div>

        {/* Recent Uploads */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
              Recent Uploads
            </h3>
            <Link 
              to="/upload" 
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
              data-testid="view-all-uploads"
            >
              Upload New <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </div>
          
          {stats.recent_uploads.length > 0 ? (
            <div className="divide-y divide-zinc-800/50">
              {stats.recent_uploads.map((dataset) => (
                <RecentUpload key={dataset.id} dataset={dataset} />
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <Database weight="thin" className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No datasets yet</p>
              <Link 
                to="/upload" 
                className="text-blue-400 hover:text-blue-300 text-sm mt-2 inline-block"
                data-testid="upload-first-dataset"
              >
                Upload your first dataset
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
